import { useState } from "react";
import {
  Box,
  Chip,
  ClickAwayListener,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { InfoOutlined as InfoOutlinedIcon } from "@mui/icons-material";
import { format, parseISO } from "date-fns";

/**
 * Record Info legend rows (PEPDEV-7476). Action labels match the displayed
 * "operation object_type" combination, including the OUTREACH suffix (PEPDEV-7477).
 */
const RECORD_INFO_LEGEND = [
  ["CANCEL OUTREACH", "Protocol #, wave name, conducted date"],
  ["CANCEL OUTREACH_SCHEDULE", "Protocol #, Parent wave name"],
  ["CREATE OUTREACH", "Protocol #, wave name / one time or recurring"],
  ["DOWNLOAD OUTREACH", "Protocol #, wave name, conducted date"],
  ["EDIT PROSPECT", "MRN"],
  ["EDIT STUDY", "Protocol #"],
  ["IMPORT IMPORT_REQUEST", "Protocol #, File Name"],
  ["LOGIN USER", "Login Name"],
  ["PAUSE IMPORT_REQUEST", "Protocol #, File Name"],
  ["START IMPORT_REQUEST", "Protocol #, File Name"],
  ["STOP IMPORT_REQUEST", "Protocol #, File Name"],
];

/**
 * Legend content rendered inside the Record Info header tooltip.
 */
const RecordInfoLegend = ({ t }) => (
  <Box sx={{ p: 0.5 }}>
    <Typography variant="caption" sx={{ display: "block", mb: 1 }}>
      {t("activityLog.recordInfoLegend.intro")}
    </Typography>
    <Box component="table" sx={{ borderCollapse: "collapse", width: "100%" }}>
      <Box component="thead">
        <Box component="tr">
          <Box
            component="th"
            sx={{
              textAlign: "left",
              pr: 2,
              pb: 0.5,
              borderBottom: "1px solid rgba(255,255,255,0.3)",
            }}
          >
            {t("activityLog.recordInfoLegend.actionHeader")}
          </Box>
          <Box
            component="th"
            sx={{
              textAlign: "left",
              pb: 0.5,
              borderBottom: "1px solid rgba(255,255,255,0.3)",
            }}
          >
            {t("activityLog.recordInfoLegend.recordInfoHeader")}
          </Box>
        </Box>
      </Box>
      <Box component="tbody">
        {RECORD_INFO_LEGEND.map(([action, info]) => (
          <Box component="tr" key={action}>
            <Box
              component="td"
              sx={{ pr: 2, py: 0.25, verticalAlign: "top", whiteSpace: "nowrap" }}
            >
              {action}
            </Box>
            <Box component="td" sx={{ py: 0.25, verticalAlign: "top" }}>
              {info}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
);

/**
 * Record Info column header with a click-triggered legend tooltip (PEPDEV-7476).
 */
const RecordInfoHeader = ({ t }) => {
  const [open, setOpen] = useState(false);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <span>{t("activityLog.column.recordInfo")}</span>
      <ClickAwayListener onClickAway={() => setOpen(false)}>
        <Tooltip
          open={open}
          onClose={() => setOpen(false)}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          placement="bottom-start"
          title={<RecordInfoLegend t={t} />}
          slotProps={{ tooltip: { sx: { maxWidth: 420 } } }}
        >
          <IconButton
            size="small"
            aria-label={t("activityLog.recordInfoLegend.ariaLabel")}
            onClick={(event) => {
              event.stopPropagation();
              setOpen((prev) => !prev);
            }}
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </ClickAwayListener>
    </Box>
  );
};


/**
 * Get status chip color based on status value
 */
const getStatusColor = (status) => {
  switch (status) {
    case "SUCCESS":
      return "success";
    case "FAILURE":
      return "error";
    case "PENDING":
      return "warning";
    default:
      return "default";
  }
};

/**
 * Format timestamp to readable format
 */
const formatTimestamp = (timestamp) => {
  if (!timestamp) return "";
  try {
    return format(parseISO(timestamp), "MM/dd/yyyy hh:mm:ss a");
  } catch (error) {
    return timestamp;
  }
};

/**
 * Column configuration for Activity Log table
 */
export const defaultTableColumnHeaders = (t, onViewJson) => [
  {
    field: "timestamp",
    headerName: t("activityLog.column.timestamp"),
    flex: 1.2,
    minWidth: 180,
    sortable: true,
    renderCell: (params) => (
      <Tooltip title={params.value || ""} placement="top">
        <span>{formatTimestamp(params.value)}</span>
      </Tooltip>
    ),
  },
  {
    field: "userName",
    headerName: t("activityLog.column.user"),
    flex: 1,
    minWidth: 150,
    sortable: true,
    renderCell: (params) => (
      <Tooltip title={params.row.userEmail || params.row.userId || ""} placement="top">
        <span>{params.value || params.row.userSunetId || params.row.userId || "—"}</span>
      </Tooltip>
    ),
  },
  {
    field: "action",
    headerName: t("activityLog.column.action"),
    flex: 1.1,
    minWidth: 170,
    sortable: true,
    renderCell: (params) => (
      <Tooltip title={params.value || ""} placement="top">
        <span>{params.value || "—"}</span>
      </Tooltip>
    ),
  },
  {
    field: "recordInfo",
    headerName: t("activityLog.column.recordInfo"),
    flex: 1.5,
    minWidth: 200,
    sortable: false,
    renderHeader: () => <RecordInfoHeader t={t} />,
    renderCell: (params) => (
      <Tooltip title={params.value || ""} placement="top">
        <span
          style={{
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {params.value || "—"}
        </span>
      </Tooltip>
    ),
  },
  {
    field: "status",
    headerName: t("activityLog.column.status"),
    flex: 0.8,
    minWidth: 100,
    sortable: true,
    renderCell: (params) => (
      <Chip
        label={params.value}
        size="small"
        color={getStatusColor(params.value)}
      />
    ),
  },
  {
    field: "data",
    headerName: t("activityLog.column.data"),
    flex: 2,
    minWidth: 260,
    sortable: false,
    renderCell: (params) => {
      const displayValue =
        typeof params.value === "string"
          ? params.value
          : params.value
          ? JSON.stringify(params.value)
          : "";

      return (
        <Tooltip title={displayValue || t("activityLog.jsonModal.noData")} placement="top">
          <span
            role={displayValue ? "button" : undefined}
            tabIndex={displayValue ? 0 : undefined}
            onClick={displayValue ? () => onViewJson(params.row) : undefined}
            onKeyDown={
              displayValue
                ? (event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      onViewJson(params.row);
                    }
                  }
                : undefined
            }
            style={{
              display: "block",
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              cursor: displayValue ? "pointer" : "default",
              color: displayValue ? "inherit" : "#757575",
            }}
          >
            {displayValue || "—"}
          </span>
        </Tooltip>
      );
    },
  },
];
